/**
 * One organization: its paired space, its membership, and its subsidiaries.
 *
 * §3.2 is rendered structurally rather than stated once — membership and access
 * are two panels that do not talk to each other, and adding a member writes no
 * grant. §3.3 is rendered as the bulk action it describes: "grant to all
 * subsidiaries" writes N explicit rows and says how many, because the whole
 * design is that convenience lives in the action while the data stays explicit.
 */

import { Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { Form } from 'react-aria-components';
import {
  useAddMember,
  useAddRelationship,
  useEndRelationship,
  useGrantToSubsidiaries,
  useOrganization,
  usePeoplePicker,
  useRemoveMember,
  useSubsidiaries,
} from '../../api/queries.ts';
import {
  type GrantEffect,
  type OrgRelationshipType,
  type SubsidiaryGrantResponse,
  USER_ROLES,
} from '../../api/types.ts';
import { CAPABILITY_OPTIONS } from '../../components/capabilities.ts';
import { formatDate } from '../../components/format.ts';
import {
  RELATIONSHIP_LABELS,
  RELATIONSHIP_OPTIONS,
  RelationshipBadges,
} from '../../components/relationships.tsx';
import { Badge, RoleBadge, roleLabel, StatusBadge } from '../../components/ui/badge.tsx';
import { Button, LinkButton } from '../../components/ui/button.tsx';
import { Dialog, DialogBody, DialogFooter } from '../../components/ui/dialog.tsx';
import { Alert, ErrorState, Loading, PageError } from '../../components/ui/feedback.tsx';
import { Select, TextField } from '../../components/ui/field.tsx';
import {
  Card,
  CardHeader,
  Definition,
  DefinitionList,
  Mono,
  PageHeader,
} from '../../components/ui/layout.tsx';
import { Table, TableEmpty, TBody, TD, TH, THead, TR } from '../../components/ui/table.tsx';

function AddMemberForm({
  organizationId,
  onClose,
}: {
  organizationId: string;
  onClose: () => void;
}) {
  const add = useAddMember(organizationId);
  const picker = usePeoplePicker();
  const [personId, setPersonId] = useState('');
  const [title, setTitle] = useState('');

  const options = (picker.data?.people ?? []).map((person) => ({
    value: person.id,
    label: person.name,
    description: `${person.email} · ${roleLabel(person.role)}`,
  }));

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
        add.mutate(
          { personId, ...(title.trim() ? { title: title.trim() } : {}) },
          { onSuccess: onClose },
        );
      }}
    >
      <DialogBody>
        <Alert tone="info">
          This records who someone works for. It grants nothing — access is a separate edge and a
          separate action.
        </Alert>
        <Select
          label="Person"
          placeholder="Choose a person…"
          options={options}
          selectedKey={personId || null}
          onSelectionChange={setPersonId}
          isRequired
        />
        <TextField label="Title" value={title} onChange={setTitle} placeholder="Optional" />
        {add.isError ? <ErrorState error={add.error} /> : null}
      </DialogBody>
      <DialogFooter>
        <Button variant="tertiary" onPress={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" isPending={add.isPending} isDisabled={!personId}>
          Add member
        </Button>
      </DialogFooter>
    </Form>
  );
}

function SubsidiaryGrantForm({
  organizationId,
  subsidiaryCount,
  onClose,
}: {
  organizationId: string;
  subsidiaryCount: number;
  onClose: () => void;
}) {
  const bulk = useGrantToSubsidiaries(organizationId);
  const picker = usePeoplePicker();
  const [principalType, setPrincipalType] = useState<'person' | 'role'>('person');
  const [principalId, setPrincipalId] = useState('');
  const [capability, setCapability] = useState('document.view');
  const [effect, setEffect] = useState<GrantEffect>('allow');
  const [includeParent, setIncludeParent] = useState<'yes' | 'no'>('no');
  const [result, setResult] = useState<SubsidiaryGrantResponse | null>(null);

  const peopleOptions = (picker.data?.people ?? []).map((person) => ({
    value: person.id,
    label: person.name,
    description: person.email,
  }));

  if (result) {
    return (
      <>
        <DialogBody>
          <Alert tone="success" title={`${result.grants.length} explicit row(s) written`}>
            Across {result.spacesTargeted} space(s). Nothing cascaded — each subsidiary got its own
            row, which is why adding a subsidiary later cannot silently widen anyone's access.
          </Alert>
          {result.alreadyPresentOnSpaceIds.length > 0 ? (
            <p className="text-sm text-tertiary">
              {result.alreadyPresentOnSpaceIds.length} space(s) already had that exact grant, so
              nothing was written there.
            </p>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="primary" onPress={onClose}>
            Done
          </Button>
        </DialogFooter>
      </>
    );
  }

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
        bulk.mutate(
          {
            principalType,
            principalId,
            capability,
            effect,
            includeParent: includeParent === 'yes',
          },
          { onSuccess: setResult },
        );
      }}
    >
      <DialogBody>
        <Alert tone="info">
          Writes one grant row per active subsidiary space, in one transaction. If you cannot grant
          on any one of them, none are written.
        </Alert>
        <Select
          label="Grant to"
          options={[
            { value: 'person' as const, label: 'A person' },
            { value: 'role' as const, label: 'A role' },
          ]}
          selectedKey={principalType}
          onSelectionChange={(value) => {
            setPrincipalType(value);
            setPrincipalId('');
          }}
        />
        <Select
          label={principalType === 'person' ? 'Person' : 'Role'}
          placeholder="Choose…"
          options={
            principalType === 'person'
              ? peopleOptions
              : USER_ROLES.map((role) => ({ value: role as string, label: roleLabel(role) }))
          }
          selectedKey={principalId || null}
          onSelectionChange={setPrincipalId}
          isRequired
        />
        <Select
          label="Capability"
          options={CAPABILITY_OPTIONS}
          selectedKey={capability}
          onSelectionChange={setCapability}
        />
        <Select
          label="Effect"
          options={[
            { value: 'allow' as const, label: 'allow' },
            { value: 'deny' as const, label: 'deny' },
          ]}
          selectedKey={effect}
          onSelectionChange={setEffect}
        />
        <Select
          label="Include the parent's own space"
          options={[
            { value: 'no' as const, label: `No — the ${subsidiaryCount} subsidiaries only` },
            { value: 'yes' as const, label: 'Yes — the parent space too' },
          ]}
          selectedKey={includeParent}
          onSelectionChange={setIncludeParent}
        />
        {bulk.isError ? <ErrorState error={bulk.error} /> : null}
      </DialogBody>
      <DialogFooter>
        <Button variant="tertiary" onPress={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          isPending={bulk.isPending}
          isDisabled={!principalId}
        >
          Write the rows
        </Button>
      </DialogFooter>
    </Form>
  );
}

/**
 * §3.3's conversion, as one action. The client option is the one that does
 * anything structural: it mints the organization's space and the portal home
 * beneath it, in the transaction that writes the relationship row.
 */
function AddRelationshipForm({
  organizationId,
  onClose,
}: {
  organizationId: string;
  onClose: () => void;
}) {
  const add = useAddRelationship(organizationId);
  const [type, setType] = useState<OrgRelationshipType>('client');

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
        add.mutate({ type }, { onSuccess: onClose });
      }}
    >
      <DialogBody>
        <Select
          label="Relationship"
          options={RELATIONSHIP_OPTIONS}
          selectedKey={type}
          onSelectionChange={(value) => setType(value as OrgRelationshipType)}
        />
        {type === 'client' ? (
          <Alert tone="warning" title="This creates two spaces and narrows who can reach this">
            The organization's space and its portal home are written in the same transaction. From
            then on, reaching this record means reaching that space — holding org.manage alone stops
            being enough, for you and for everybody else.
          </Alert>
        ) : null}
        {add.isError ? <ErrorState error={add.error} /> : null}
      </DialogBody>
      <DialogFooter>
        <Button variant="tertiary" onPress={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" isPending={add.isPending}>
          {type === 'client' ? 'Convert and create the spaces' : 'Add the relationship'}
        </Button>
      </DialogFooter>
    </Form>
  );
}

export function OrganizationDetailPage() {
  const { organizationId } = useParams({
    from: '/_authed/_employee/organizations/$organizationId',
  });
  const query = useOrganization(organizationId);
  const subsidiaries = useSubsidiaries(organizationId);
  const removeMember = useRemoveMember(organizationId);
  const [addingMember, setAddingMember] = useState(false);
  const [bulkGranting, setBulkGranting] = useState(false);
  const [converting, setConverting] = useState(false);
  const [ending, setEnding] = useState<string | null>(null);
  const endRelationship = useEndRelationship(organizationId);

  if (query.isPending) return <Loading />;
  if (query.isError) {
    return (
      <PageError
        error={query.error}
        title="Organization"
        back={{ href: '/organizations', label: 'Back to organizations' }}
      />
    );
  }

  const { organization, space, relationships, members } = query.data;
  const subsidiaryList = subsidiaries.data?.subsidiaries ?? [];

  return (
    <>
      <PageHeader
        title={organization.name}
        actions={
          space ? (
            <LinkButton variant="secondary" href={`/spaces/${space.id}`}>
              Open its space
            </LinkButton>
          ) : (
            <Button variant="primary" onPress={() => setConverting(true)}>
              Add a relationship
            </Button>
          )
        }
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <h2 className="mb-4 text-md font-semibold text-primary">Organization</h2>
          <DefinitionList>
            <Definition term="Status">
              <Badge tone={organization.status === 'active' ? 'success' : 'gray'}>
                {organization.status}
              </Badge>
            </Definition>
            <Definition term="Parent">
              {organization.parentOrgId ? (
                <Link
                  to="/organizations/$organizationId"
                  params={{ organizationId: organization.parentOrgId }}
                >
                  View parent
                </Link>
              ) : (
                <span className="text-tertiary">None — a top-level organization</span>
              )}
            </Definition>
            <Definition term="Relationship">
              <RelationshipBadges relationships={relationships} />
            </Definition>
            <Definition term="Created">{formatDate(organization.createdAt)}</Definition>
            <Definition term="Identifier">
              <Mono>{organization.id}</Mono>
            </Definition>
          </DefinitionList>

          {relationships.length > 0 ? (
            <ul className="mt-5 flex flex-col gap-2 border-t border-secondary pt-4 text-sm">
              {relationships.map((relationship) => (
                <li key={relationship.id} className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-primary">
                    {RELATIONSHIP_LABELS[relationship.type]}
                  </span>
                  <span className="text-tertiary">
                    {formatDate(relationship.startedOn)} —{' '}
                    {relationship.endedOn ? formatDate(relationship.endedOn) : 'open'}
                  </span>
                  {relationship.endedOn === null && relationship.type !== 'internal' ? (
                    <Button size="sm" variant="tertiary" onPress={() => setEnding(relationship.id)}>
                      End it
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </Card>

        {space ? (
          <Card>
            <h2 className="mb-1 text-md font-semibold text-primary">Its space</h2>
            <p className="mb-4 text-sm text-tertiary">
              Created in the same transaction as the client relationship. An organization that has
              ever been a client keeps its space permanently — an engagement ending does not unmake
              what was delivered.
            </p>
            <DefinitionList>
              <Definition term="Name">
                <Link to="/spaces/$spaceId" params={{ spaceId: space.id }}>
                  {space.name}
                </Link>
              </Definition>
              <Definition term="Kind">
                <Badge tone="outline">{space.kind}</Badge>
              </Definition>
              <Definition term="Parent space">
                {space.parentSpaceId ? <Mono>{space.parentSpaceId}</Mono> : 'Root of the tree'}
              </Definition>
              <Definition term="Identifier">
                <Mono>{space.id}</Mono>
              </Definition>
            </DefinitionList>
          </Card>
        ) : (
          <Card>
            <h2 className="mb-1 text-md font-semibold text-primary">No space</h2>
            <p className="mb-4 text-sm text-tertiary">
              This organization has never been a client, so nothing was minted in the space tree — a
              pipeline entry does not need a container with an ACL.
            </p>
            <p className="text-sm text-secondary">
              Everyone whose role carries <span className="font-mono">org.manage</span> can see and
              edit this record. Per-record access control begins at conversion: adding a client
              relationship creates the space and its portal home in one transaction, and from then
              on reaching this organization means reaching that space.
            </p>
          </Card>
        )}
      </div>

      <Card padded={false}>
        <CardHeader
          title="Membership"
          description="Who works for this company. Distinct from access: nothing here grants anything, and removing someone here revokes nothing."
          actions={
            <Button size="sm" variant="secondary" onPress={() => setAddingMember(true)}>
              Add member
            </Button>
          }
        />
        <Table>
          <THead>
            <TR>
              <TH>Person</TH>
              <TH>Title</TH>
              <TH>Role</TH>
              <TH>Status</TH>
              <TH className="text-right">&nbsp;</TH>
            </TR>
          </THead>
          <TBody>
            {members.length === 0 ? (
              <TableEmpty colSpan={5}>No members recorded.</TableEmpty>
            ) : (
              members.map((member) => (
                <TR key={member.personId} className="hover:bg-secondary">
                  <TD>
                    <Link
                      to="/admin/people/$personId"
                      params={{ personId: member.personId }}
                      className="font-medium text-primary no-underline hover:underline"
                    >
                      {member.name}
                    </Link>
                    <span className="block text-xs text-tertiary">{member.email}</span>
                  </TD>
                  <TD>{member.title ?? <span className="text-tertiary">—</span>}</TD>
                  <TD>
                    {member.role ? (
                      <RoleBadge role={member.role} />
                    ) : (
                      <span className="text-tertiary">—</span>
                    )}
                  </TD>
                  <TD>
                    {member.status ? (
                      <StatusBadge status={member.status} />
                    ) : (
                      // A contact: affiliated here, and unable to sign in
                      // anywhere. §3.2's distinction, now with an example.
                      <Badge tone="outline">No access</Badge>
                    )}
                  </TD>
                  <TD className="text-right">
                    <span className="flex justify-end gap-2">
                      <Link
                        to="/schema/access/people/$personId"
                        params={{ personId: member.personId }}
                        className="text-sm link"
                      >
                        What they reach
                      </Link>
                      <Button
                        size="sm"
                        variant="tertiary"
                        className="text-error-primary"
                        onPress={() => removeMember.mutate(member.personId)}
                      >
                        Remove
                      </Button>
                    </span>
                  </TD>
                </TR>
              ))
            )}
          </TBody>
        </Table>
      </Card>

      <Card padded={false}>
        <CardHeader
          title="Subsidiaries"
          description="Full organizations with their own space, because billing happens at the subsidiary level. They are siblings in the space tree, so grants on this organization's space do not reach them."
          actions={
            subsidiaryList.length > 0 ? (
              <Button size="sm" variant="secondary" onPress={() => setBulkGranting(true)}>
                Grant to all subsidiaries
              </Button>
            ) : null
          }
        />
        <Table>
          <THead>
            <TR>
              <TH>Subsidiary</TH>
              <TH>Its space</TH>
              <TH>Status</TH>
            </TR>
          </THead>
          <TBody>
            {subsidiaries.isPending ? (
              <TableEmpty colSpan={3}>Loading…</TableEmpty>
            ) : subsidiaryList.length === 0 ? (
              <TableEmpty colSpan={3}>No subsidiaries.</TableEmpty>
            ) : (
              subsidiaryList.map((subsidiary) => (
                <TR key={subsidiary.id} className="hover:bg-secondary">
                  <TD>
                    <Link
                      to="/organizations/$organizationId"
                      params={{ organizationId: subsidiary.id }}
                      className="font-medium text-primary no-underline hover:underline"
                    >
                      {subsidiary.name}
                    </Link>
                  </TD>
                  <TD>
                    {subsidiary.space ? (
                      <Link to="/spaces/$spaceId" params={{ spaceId: subsidiary.space.id }}>
                        {subsidiary.space.name}
                      </Link>
                    ) : (
                      <span className="text-sm text-tertiary">None — not a client</span>
                    )}
                  </TD>
                  <TD>
                    <Badge tone={subsidiary.status === 'active' ? 'success' : 'gray'}>
                      {subsidiary.status}
                    </Badge>
                  </TD>
                </TR>
              ))
            )}
          </TBody>
        </Table>
      </Card>

      <Dialog
        title="Add a relationship"
        description="A client relationship creates the space pair in the same transaction. The others are records."
        isOpen={converting}
        onOpenChange={setConverting}
      >
        {(close) => <AddRelationshipForm organizationId={organizationId} onClose={close} />}
      </Dialog>

      <Dialog
        title="End this engagement"
        description="A commercial fact and nothing more. The space stays, and so does everything in it."
        isOpen={ending !== null}
        onOpenChange={(open) => setEnding(open ? ending : null)}
      >
        {(close) => (
          <>
            <DialogBody>
              <p className="text-sm text-secondary">
                Ending a relationship withdraws no access. Revoking grants is a separate action with
                its own event, and the two are deliberately not coupled — an engagement that ends on
                paper often has a month of wind-down reading behind it.
              </p>
              {endRelationship.isError ? <ErrorState error={endRelationship.error} /> : null}
            </DialogBody>
            <DialogFooter>
              <Button variant="tertiary" onPress={close}>
                Cancel
              </Button>
              <Button
                variant="primary"
                isPending={endRelationship.isPending}
                onPress={() => {
                  if (!ending) return;
                  endRelationship.mutate(
                    { relationshipId: ending, endedOn: new Date().toISOString().slice(0, 10) },
                    {
                      onSuccess: () => {
                        setEnding(null);
                        close();
                      },
                    },
                  );
                }}
              >
                End it today
              </Button>
            </DialogFooter>
          </>
        )}
      </Dialog>

      <Dialog title="Add a member" isOpen={addingMember} onOpenChange={setAddingMember}>
        {(close) => <AddMemberForm organizationId={organizationId} onClose={close} />}
      </Dialog>

      <Dialog
        title="Grant to all subsidiaries"
        description="N explicit rows, never a cascade."
        isOpen={bulkGranting}
        onOpenChange={setBulkGranting}
        size="lg"
      >
        {(close) => (
          <SubsidiaryGrantForm
            organizationId={organizationId}
            subsidiaryCount={subsidiaryList.length}
            onClose={close}
          />
        )}
      </Dialog>
    </>
  );
}
